// Store links, Smart App Banner mapping and click tracking for the web → app funnel.
//
// Every outbound store link is tagged so installs can be attributed to the site:
// - App Store: provider token `pt` + campaign token `ct` (App Store Connect →
//   App Analytics → Sources → Campaigns). `ct` is "lp-<placement>-<page>".
// - Google Play: an install `referrer` with UTM params (Play Console →
//   Statistics / acquisition reports, and Firebase/GA4 first_open attribution).

export const APPLE_PROVIDER_TOKEN = '126723562';

export const APPS = {
  crowns: {
    key: 'crowns',
    name: 'Crowns',
    fullName: 'Crowns Logic Puzzle',
    iosId: '6504047514',
    iosSlug: 'crowns-logic-game',
    androidPackage: 'io.github.benmiller3.crowns',
    icon: '/assets/logos/crowns_logo_256.webp',
    pitch: '1,500+ Crowns puzzles, a new daily puzzle, hints and streaks. Free on iPhone, iPad and Android.',
  },
  bsp: {
    key: 'bsp',
    name: 'Binary Sudoku+',
    fullName: 'Binary Sudoku+',
    iosId: '6738749684',
    iosSlug: 'binary-sudoku',
    androidPackage: 'io.github.benmiller3.symmetrix',
    icon: '/assets/logos/symmetrix_logo.webp',
    pitch: '1,300+ binary puzzles plus timed challenges. Free on iPhone, iPad and Android.',
  },
  dlp: {
    key: 'dlp',
    name: 'Daily Logic Puzzles',
    fullName: 'Daily Logic Puzzles',
    iosId: '6749245788',
    iosSlug: 'daily-logic-puzzles',
    androidPackage: 'ca.sparkandspiral.daily_logic_puzzles',
    icon: '/assets/logos/dailylogicpuzzles_logo.webp',
    pitch: 'Eight fresh logic puzzles every day: Crowns, Sudoku, Mine Finder, Aquarium and more. Free on iPhone and Android.',
  },
  // Android only for now (no App Store listing yet): StoreCTA shows just the
  // Google Play badge and hides itself on iPhone/iPad. Its Play link uses a
  // fixed install referrer so the Mine Finder page's installs are easy to find.
  minefinder: {
    key: 'minefinder',
    name: 'Mine Finder',
    fullName: 'Mine Finder: No Guess Logic',
    androidPackage: 'ca.sparkandspiral.noguessminesweeper',
    androidOnly: true,
    playUtm: { utm_source: 'logicpuzzles', utm_medium: 'web', utm_campaign: 'minefinder_page' },
    icon: '/minefinder/icons/Icon-192.png',
    pitch: '500 boards that never need a guess, a daily puzzle with streaks, and hints that explain the logic. Free on Android.',
  },
  // Legacy single-game apps. Only used on their own /apps/* pages; every game
  // page promotes Crowns, Binary Sudoku+ or Daily Logic Puzzles instead.
  aquarium: {
    key: 'aquarium', name: 'Aquarium', fullName: 'Aquarium Logic Puzzle',
    iosId: '6742514141', iosSlug: 'aquarium-logic-puzzle', androidPackage: 'io.github.benmiller3.aquifer',
  },
  dotstream: {
    key: 'dotstream', name: 'Dot Stream', fullName: 'Dot Stream Logic Puzzle',
    iosId: '6744259349', iosSlug: 'dot-stream', androidPackage: 'io.github.benmiller3.neon_line_connect',
  },
  palettepath: {
    key: 'palettepath', name: 'Color Flood', fullName: 'Color Flood',
    iosId: '6738512610', iosSlug: 'palette-path', androidPackage: 'io.github.benmiller3.flood_fill',
  },
  // Newer single-game apps, listed on /apps. The androidOnly ones have no App
  // Store listing yet: drop the flag once the iOS build is live.
  fleet: {
    key: 'fleet', name: 'Fleet', fullName: 'Fleet Logic Puzzle',
    iosId: '6759056298', iosSlug: 'fleet-logic-puzzle', androidPackage: 'ca.sparkandspiral.fleetlogicpuzzle',
  },
  nonogram: {
    key: 'nonogram', name: 'Nonogram Puzzles', fullName: 'Nonogram Puzzles: Pixel Logic',
    androidPackage: 'ca.sparkandspiral.nonogram', androidOnly: true,
  },
  calcudoku: {
    key: 'calcudoku', name: 'Calcudoku', fullName: 'Calcudoku: Math Logic Puzzle',
    androidPackage: 'ca.sparkandspiral.calcudoku', androidOnly: true,
  },
  domino: {
    key: 'domino', name: 'Domino Puzzle', fullName: 'Domino Puzzle: Logic Game',
    androidPackage: 'ca.sparkandspiral.dominologic', androidOnly: true,
  },
  logicgrid: {
    key: 'logicgrid', name: 'Logic Grid', fullName: 'Logic Grid: Deduction Puzzles',
    androidPackage: 'ca.sparkandspiral.logicgrid', androidOnly: true,
  },
  buzzbloom: {
    key: 'buzzbloom', name: 'Buzzbloom', fullName: 'Buzzbloom: Bee Logic Puzzle',
    androidPackage: 'ca.sparkandspiral.buzzbloom', androidOnly: true,
  },
};

const slugify = (value) => String(value || '')
  .toLowerCase()
  .replace(/^\/+|\/+$/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'home';

// App Store campaign tokens are limited to 40 characters.
export function campaignToken(placement, page) {
  return `lp-${slugify(placement)}-${slugify(page)}`.slice(0, 40);
}

// App Store link with a provider token and the given campaign token.
function appStoreCampaignUrl(appKey, ct, slug = APPS[appKey].iosSlug) {
  const app = APPS[appKey];
  const params = new URLSearchParams({ pt: APPLE_PROVIDER_TOKEN, ct, mt: '8' });
  return `https://apps.apple.com/app/${slug}/id${app.iosId}?${params.toString()}`;
}

// Google Play link with the given UTM params as the install referrer.
function playStoreReferrerUrl(appKey, utm) {
  const app = APPS[appKey];
  const referrer = new URLSearchParams(utm).toString();
  const params = new URLSearchParams({ id: app.androidPackage, referrer });
  return `https://play.google.com/store/apps/details?${params.toString()}`;
}

export function appStoreUrl(appKey, placement, page) {
  return appStoreCampaignUrl(appKey, campaignToken(placement, page));
}

export function playStoreUrl(appKey, placement, page) {
  if (APPS[appKey].playUtm) return playStoreReferrerUrl(appKey, APPS[appKey].playUtm);
  return playStoreReferrerUrl(appKey, {
    utm_source: 'logicpuzzles.ca',
    utm_medium: 'web',
    utm_campaign: slugify(page),
    utm_content: slugify(placement),
  });
}

export function storeUrl(appKey, store, placement, page) {
  return store === 'google_play'
    ? playStoreUrl(appKey, placement, page)
    : appStoreUrl(appKey, placement, page);
}

// 'ios' | 'android' | 'desktop'. Call only in the browser (after mount), never
// during render, so prerendered HTML and the first client render match.
export function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // iPadOS 13+ reports itself as a Mac; touch support gives it away.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return 'ios';
  return 'desktop';
}

// GA4 event for every store-link click. gtag is defined in index.html on every
// page; Consent Mode v2 (see CookieConsent.jsx) decides whether GA4 may store
// cookies or only send cookieless pings.
// `params` adds extra event params. `onSent` (optional) is GA4's event_callback:
// it runs once gtag.js has taken the hit (it may still be batched until the page
// unloads). It never runs if gtag.js is blocked, so callers that navigate away
// afterwards need their own timeout too (see src/pages/GoPage.jsx).
export function trackStoreClick({ app, store, placement, page, params, onSent }) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'store_click', {
        app_name: app,
        store,
        placement,
        page_path: page || window.location.pathname,
        ...params,
        transport_type: 'beacon',
        ...(onSent ? { event_callback: onSent } : {}),
      });
    }
  } catch (e) {
    /* never block navigation on analytics */
  }
}

// Link-in-bio redirects: each social profile links to logicpuzzles.ca/go/<platform>
// (src/pages/GoPage.jsx), which sends phones to the Crowns store listing and
// everyone else to the web game, tagged with the platform:
// - App Store: ct "soc-<platform>"
// - Google Play: utm_source=<platform>, utm_medium=social, utm_campaign=bio
// - Web: /crowns-puzzle with the same UTM params
// These pages are noindexed and left out of the sitemap (scripts/routes.mjs).
export const SOCIAL_PLATFORMS = [
  'youtube', 'tiktok', 'instagram', 'facebook', 'reddit',
  'discord', 'bluesky', 'threads', 'x', 'email',
];

export function socialBioLinks(platform) {
  const source = slugify(platform);
  const utm = { utm_source: source, utm_medium: 'social', utm_campaign: 'bio' };
  return {
    // Campaign tokens here stay within 30 characters.
    app_store: appStoreCampaignUrl('crowns', `soc-${source}`.slice(0, 30), 'apple-store'),
    google_play: playStoreReferrerUrl('crowns', utm),
    web: `/crowns-puzzle?${new URLSearchParams(utm).toString()}`,
  };
}

// Which app a page should promote (Smart App Banner + CTAs).
// Crowns pages → Crowns; Binary Sudoku pages → Binary Sudoku+;
// other puzzle pages → Daily Logic Puzzles; everything else → Crowns.
export function appForPath(pathname = '/') {
  const p = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  if (p === '/apps/aquarium') return 'aquarium';
  if (p === '/apps/dotstream') return 'dotstream';
  if (p === '/apps/palettepath') return 'palettepath';
  if (p === '/apps/daily-logic-puzzles') return 'dlp';
  if (/crowns/.test(p)) return 'crowns';
  if (/binary-?sudoku|symmetrix/.test(p)) return 'bsp';
  if (/^\/(games|play|printable)\//.test(p) || p === '/logic-games') return 'dlp';
  return 'crowns';
}
